import React, { useMemo, useRef, useState } from 'react';

import { useDraggable } from '@dnd-kit/core';
import cn from 'classnames';

import { useIsOverflowing } from '@shared/hooks';
import { Accordion, Flex, Icon, UiText } from '@shared/ui';
import { useOrdersRecognitionContext } from '@widgets/OrdersRecognition/lib/hooks/useOrdersRecognitionContext';
import { TOrderItem } from '@widgets/OrdersRecognition/model/types';
import { DraggableEl } from '@widgets/OrdersRecognition/ui/Order/DraggableEl';
import { OrderAvatar } from '@widgets/OrdersRecognition/ui/Order/OrderAvatar';
import { OrderPosition } from '@widgets/OrdersRecognition/ui/Order/OrderPosition';

import cls from './Order.module.css';

interface IOrderProps {
    orderData: TOrderItem;
    isCanEdit: boolean;
    className?: string;
}
export const Order = ({ orderData, isCanEdit, className }: IOrderProps) => {
    const {
        id,
        displayString: name,
        displayStringForExcess,
        documentDate,
        sum,
        positions: orderPositions,
        createdBy,
        supplier,
        isFullyUsed,
        currency,
        attachment: fileInfo,
    } = orderData;
    const [isOpen, setIsOpen] = useState(false);
    const orderTitleRef = useRef<HTMLDivElement>(null);
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `order-${id}`,
        data: {
            type: 'order',
            item: { id, name, sum, supplier, displayStringForExcess, documentDate },
        },
    });

    const { openDeletePopup, changeCurrentOrderId, onShowLightBox } = useOrdersRecognitionContext();

    const { isOverflowing } = useIsOverflowing({ elRef: orderTitleRef });

    const onDeleteHandler = async () => {
        changeCurrentOrderId(id);
        openDeletePopup();
    };
    const onAccordionToggle = () => {
        if (orderPositions.length > 0) {
            setIsOpen((prev) => !prev);
        }
    };

    const filesForLightbox = useMemo(
        () => [
            {
                id,
                fileSrc: fileInfo?.url,
                mimeType: fileInfo?.mimeType,
                fileName: fileInfo?.fullName,
            },
        ],
        [fileInfo, id]
    );

    return (
        <Flex fullWidth justify="between" className={cn(cls.OrderWrapper, className)}>
            <Accordion
                title={
                    <Flex
                        gap={8}
                        className={cn(
                            cls.Order,
                            isDragging && cls.isDragging,
                            isFullyUsed && cls.isProceed,
                            className
                        )}
                        ref={setNodeRef}
                    >
                        <OrderAvatar userData={createdBy} />
                        {isCanEdit && (
                            <DraggableEl {...listeners} {...attributes} isProceed={isFullyUsed} />
                        )}
                        <UiText
                            variant="mediumPlain"
                            tag="span"
                            className={cls.OrderTitleName}
                            ref={orderTitleRef}
                            title={isOverflowing ? name : ''}
                        >
                            {name}
                        </UiText>
                        <span
                            className={cls.DocumentIcon}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onShowLightBox({ id, newFiles: filesForLightbox });
                            }}
                            // onClick={(e) => {
                            //     e.stopPropagation();
                            //     onShowLightBox({
                            //         id,
                            //         newFiles: filesForLightbox,
                            //     });
                            // }}
                            title={fileInfo?.fullName}
                        >
                            <Icon iconName="document" width={10} height={12} fill="#28B57A" />
                        </span>
                        <span className={cn(cls.AccordionIcon, isOpen && cls.OpenIcon)}>
                            {orderPositions.length > 0 && (
                                <Icon
                                    iconName="carouselNext"
                                    fill={isFullyUsed ? '#25273380' : '#252733'}
                                />
                            )}
                        </span>
                    </Flex>
                }
                className={cls.AccordionWrapper}
                titleClass={cls.OrderTitle}
                openIconClassName={cls.OpenIcon}
                contentInnerClassName={cls.AccordionInner}
                isOpen={isOpen}
                onToggle={onAccordionToggle}
            >
                <Flex
                    as="ul"
                    direction="column"
                    gap={8}
                    align="start"
                    className={cls.OrderPositionsList}
                >
                    {orderPositions?.map((pos) => (
                        <OrderPosition
                            key={pos.id}
                            orderPosition={{
                                ...pos,
                                supplier,
                                comment: name,
                                documentDate,
                                currency,
                            }}
                            isCanEdit={isCanEdit}
                        />
                    ))}
                </Flex>
            </Accordion>
            <span className={cls.deleteIcon} onClick={onDeleteHandler}>
                {isCanEdit && (
                    <Icon
                        iconName="filesDelete"
                        width={14}
                        height={14}
                        fill={isFullyUsed ? '#81899880' : '#818998'}
                    />
                )}
            </span>
        </Flex>
    );
};
